import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Experiment, Goal } from '@/types/experiment';
import { ArrowLeft, Copy, Code, Play, BarChart3, Target, Calendar, Hash, CheckCircle, Settings, Edit2, Save, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { PrismCodeBlock, PrismCodeCollection } from './PrismCodeBlock';

interface ExperimentViewProps {
  experiment: Experiment;
  goals: Goal[];
  onBack: () => void;
  onNavigateToDocumentation?: () => void;
  onExperimentUpdated?: (updatedExperiment: Experiment) => void;
  getUserId?: () => Promise<string | null>;
}

export const ExperimentView: React.FC<ExperimentViewProps> = ({ 
  experiment, 
  goals, 
  onBack, 
  onNavigateToDocumentation,
  onExperimentUpdated,
  getUserId 
}) => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [isCodeDialogOpen, setIsCodeDialogOpen] = useState(false);
  const [isUpdatingGoal, setIsUpdatingGoal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(experiment.name);

  const getGoalName = (goalId?: string) => {
    if (!goalId) return 'Unassigned';
    const goal = goals.find(g => g.id === goalId);
    return goal ? goal.name : 'Unknown Goal';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const copyToClipboard = async (code: string, label: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(label);
      toast({
        title: "Copied to clipboard",
        description: `${label} code copied successfully`,
      });
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  // Generate experiment ID for code snippets
  const experimentId = experiment.id || 'experiment-id';

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleGoalChange = async (newGoalId: string) => {
    if (!getUserId) {
      toast({
        title: "Error",
        description: "Authentication required to update experiment.",
        variant: "destructive",
      });
      return;
    }

    setIsUpdatingGoal(true);
    
    try {
      const userId = await getUserId();
      if (!userId) {
        toast({
          title: "Error",
          description: "Authentication required to update experiment.",
          variant: "destructive",
        });
        return;
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'x-user-id': userId,
      };

      // Update experiment with new goal
      const response = await fetch('/api/experiments', {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          external_id: experiment.id,
          goal_id: newGoalId === 'unassigned' ? null : newGoalId
        }),
      });

      if (response.ok) {
        const updatedExperiment = {
          ...experiment,
          goalId: newGoalId === 'unassigned' ? undefined : newGoalId
        };

        if (onExperimentUpdated) {
          onExperimentUpdated(updatedExperiment);
        }

        toast({
          title: "Success",
          description: `Experiment ${newGoalId === 'unassigned' ? 'unassigned from goal' : 'assigned to new goal'} successfully!`,
        });
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: `Failed to update experiment: ${errorData.message || 'Unknown error'}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update experiment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingGoal(false);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedName(e.target.value);
  };

  const handleStartEdit = () => {
    setEditedName(experiment.name);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedName(experiment.name);
    setIsEditing(false);
  };

  const handleSaveName = async () => {
    if (!getUserId) {
      toast({
        title: "Error",
        description: "Authentication required to update experiment.",
        variant: "destructive",
      });
      return;
    }

    setIsUpdatingGoal(true);
    
    try {
      const userId = await getUserId();
      if (!userId) {
        toast({
          title: "Error",
          description: "Authentication required to update experiment.",
          variant: "destructive",
        });
        return;
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'x-user-id': userId,
      };

      // Update experiment with new name
      const response = await fetch('/api/experiments', {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          external_id: experiment.id,
          experiment_data: {
            name: editedName,
            hypothesis: experiment.hypothesis,
            parameters: experiment.parameters
          }
        }),
      });

      if (response.ok) {
        const updatedExperiment = {
          ...experiment,
          name: editedName
        };

        if (onExperimentUpdated) {
          onExperimentUpdated(updatedExperiment);
        }

        setIsEditing(false);
        
        toast({
          title: "Success",
          description: `Experiment name updated successfully!`,
        });
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: `Failed to update experiment: ${errorData.message || 'Unknown error'}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update experiment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingGoal(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Experiments
          </Button>
          <div className="flex items-center gap-2">
            <Code className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold">Experiment Details</h1>
          </div>
        </div>
        <div className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(experiment.status)}`}>
          {experiment.status.charAt(0).toUpperCase() + experiment.status.slice(1)}
        </div>
      </div>

      {/* Experiment Header */}
      <div className="border-b pb-6">
        {isEditing ? (
          <div className="flex items-center gap-4 mb-2">
            <Input
              type="text"
              value={editedName}
              onChange={handleNameChange}
              className="text-3xl font-bold flex-1"
              disabled={isUpdatingGoal}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveName();
                } else if (e.key === 'Escape') {
                  handleCancelEdit();
                }
              }}
            />
            <Button
              onClick={handleSaveName}
              disabled={isUpdatingGoal || !editedName.trim()}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="h-4 w-4 mr-1" />
              Save
            </Button>
            <Button
              onClick={handleCancelEdit}
              disabled={isUpdatingGoal}
              size="sm"
              variant="outline"
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-4 mb-2">
            <h2 className="text-3xl font-bold text-foreground">{experiment.name}</h2>
            <Button
              onClick={handleStartEdit}
              size="sm"
              variant="outline"
            >
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </div>
        )}
        {experiment.hypothesis && (
          <p className="text-muted-foreground text-xl">{experiment.hypothesis}</p>
        )}
      </div>

      {/* Experiment Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Target className="h-5 w-5 text-primary" />
            <span className="font-medium text-muted-foreground">Associated Goal</span>
          </div>
          <div className="space-y-3">
            <p className={`text-xl font-bold ${!experiment.goalId ? 'text-amber-600' : ''}`}>
              {getGoalName(experiment.goalId)}
              {!experiment.goalId && ' ⚠️'}
            </p>
            <Select 
              value={experiment.goalId || 'unassigned'} 
              onValueChange={handleGoalChange}
              disabled={isUpdatingGoal}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a goal..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">
                  <span className="text-amber-600">⚠️ Unassigned</span>
                </SelectItem>
                {goals.map((goal) => (
                  <SelectItem key={goal.id} value={goal.id}>
                    🎯 {goal.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isUpdatingGoal && (
              <p className="text-sm text-muted-foreground">Updating...</p>
            )}
          </div>
        </div>

        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Calendar className="h-5 w-5 text-primary" />
            <span className="font-medium text-muted-foreground">Created</span>
          </div>
          <p className="text-xl font-bold">{formatDate(experiment.createdAt)}</p>
        </div>

        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Hash className="h-5 w-5 text-primary" />
            <span className="font-medium text-muted-foreground">Experiment ID</span>
          </div>
          <code className="text-sm bg-muted px-2 py-1 rounded font-mono">
            {experimentId}
          </code>
        </div>
      </div>

      {/* Parameters */}
      {experiment.parameters && Object.keys(experiment.parameters).length > 0 && (
        <div className="bg-card border rounded-lg p-6">
          <h3 className="font-semibold text-xl mb-4">Current Parameters</h3>
          <pre className="bg-muted p-4 rounded text-sm overflow-x-auto">
            {JSON.stringify(experiment.parameters, null, 2)}
          </pre>
        </div>
      )}

      {/* Results */}
      {experiment.results && (
        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-5 w-5 text-primary" />
            <h3 className="font-semibold text-xl">Results</h3>
          </div>
          <pre className="bg-muted p-4 rounded text-sm overflow-x-auto">
            {JSON.stringify(experiment.results, null, 2)}
          </pre>
        </div>
      )}

      {/* Python Client Integration */}
      <div className="bg-card border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-xl flex items-center gap-2">
              <Code className="h-5 w-5 text-primary" />
              Python Client Integration
            </h3>
            <p className="text-muted-foreground mt-1">
              Get ready-to-use Python code snippets for this experiment
            </p>
          </div>
          <Button 
            onClick={() => setIsCodeDialogOpen(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <Code className="h-4 w-4 mr-2" />
            View Code
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Play className="h-4 w-4" />
            <span>Setup & Initialize Client</span>
          </div>
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            <span>Update Parameters</span>
          </div>
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            <span>Submit Results</span>
          </div>
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            <span>Stream Live Results</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-muted border rounded-lg p-6">
        <h3 className="font-semibold text-lg mb-4">Quick Actions</h3>
        <div className="flex gap-3">
          <Button 
            onClick={() => copyToClipboard(experimentId, "Experiment ID")}
            variant="outline"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Experiment ID
          </Button>
          <Button 
            onClick={onNavigateToDocumentation}
            variant="outline"
          >
            📚 View Full Documentation
          </Button>
        </div>
      </div>

      {/* Code Dialog */}
      <Dialog open={isCodeDialogOpen} onOpenChange={setIsCodeDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Python Code for "{experiment.name}"
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="text-sm text-muted-foreground">
              Use these code snippets to interact with this experiment programmatically.
            </div>

            <PrismCodeCollection
              title="Alpha Loop Client Code"
              description="Ready-to-use code snippets for your experiment"
            >
              <PrismCodeBlock
                title="Setup & Initialize Client"
                description="Install and setup the AlphaLoop client"
                code={`# Install the AlphaLoop experiments client
pip install alphaloop-experiments

# Import and initialize the client
from alphaloop_experiments import ExperimentsClient

# Initialize with your API key
alpha_loop = ExperimentsClient(api_key="ak_your_api_key_here")

# Get this experiment
experiment = alpha_loop.get_experiment("${experimentId}")
print(f"Experiment: {experiment['name']}")
print(f"Status: {experiment['status']}")
print(f"Parameters: {experiment['parameters']}")`}
                icon={Play}
                defaultExpanded={true}
              />

              <PrismCodeBlock
                title="Update Experiment Parameters"
                description="Modify experiment configuration"
                code={`# Update parameters for this experiment
alpha_loop.update_experiment(
    experiment_id="${experimentId}",
    parameters=${JSON.stringify(experiment.parameters || {}, null, 4)}
)`}
                icon={Settings}
              />

              <PrismCodeBlock
                title="Submit Results"
                description="Send final experiment results"
                code={`# Submit final results for this experiment
results = {
    "conversion_rate": 0.12,
    "engagement_score": 8.5,
    "cost_per_acquisition": 25.50,
    "total_impressions": 15000,
    "success": True
}

alpha_loop.submit_results(
    experiment_id="${experimentId}",
    results=results,
    result_type="final"
)

print("Results submitted successfully!")`}
                icon={BarChart3}
              />

              <PrismCodeBlock
                title="Stream Live Results (While Experiment is Running)"
                description="Real-time progress updates"
                code={`# For real-time results during experiment execution
def experiment_callback(day, metrics):
    alpha_loop.submit_results(
        experiment_id="${experimentId}",
        results={
            "day": day,
            "impressions": metrics["impressions"],
            "clicks": metrics["clicks"],
            "conversions": metrics["conversions"],
            "spend": metrics["spend"]
        },
        result_type="interim"
    )

# Use in your experiment monitoring loop
for day in range(experiment_duration):
    # Your experiment monitoring code here...
    daily_metrics = get_daily_metrics()
    experiment_callback(day, daily_metrics)`}
                icon={Target}
              />
            </PrismCodeCollection>

            <div className="bg-muted rounded-lg p-4">
              <h4 className="font-semibold mb-2">Experiment Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">ID:</span>
                  <code className="ml-2 bg-background px-2 py-1 rounded">{experimentId}</code>
                </div>
                <div>
                  <span className="font-medium">Status:</span>
                  <span className="ml-2">{experiment.status}</span>
                </div>
                <div>
                  <span className="font-medium">Goal:</span>
                  <span className="ml-2">{getGoalName(experiment.goalId)}</span>
                </div>
                <div>
                  <span className="font-medium">Created:</span>
                  <span className="ml-2">{formatDate(experiment.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}; 